import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Camera, Star, LogOut, CheckCircle2, ClipboardList, Route, Bell, Plus, WifiOff } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { enqueue, getQueue, flushQueue } from "../lib/offlineQueue";
import { TIPOS_INCIDENCIA } from "../lib/constants";

const TRANSICIONES_CAMPO = { Nueva: "Asignada", Asignada: "En curso", "En curso": "Resuelta", Resuelta: "Verificada" };

function StopSheet({ stop, routeId, onDone, onClose }) {
  const [valoracion, setValoracion] = useState(5);
  const [comentario, setComentario] = useState("");
  const [file, setFile] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition((pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }), () => setCoords({ lat: stop.lat, lon: stop.lon }));
  }, [stop]);

  const confirmar = async () => {
    setConfirming(true);
    const fields = { stop_id: stop.id, lat: coords?.lat ?? stop.lat, lon: coords?.lon ?? stop.lon, valoracion, comentario };
    try {
      if (!navigator.onLine) throw { offline: true };
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post(`/routes/${routeId}/checkins`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Check-in registrado");
      onDone({ synced: true });
    } catch (e) {
      if (!navigator.onLine || !e.response) {
        await enqueue({ url: `/routes/${routeId}/checkins`, fields, file, label: `Check-in ${stop.nombre}` });
        toast.info("Sin conexión: check-in guardado, se sincronizará automáticamente");
        onDone({ synced: false, checkin: { lat: fields.lat, lon: fields.lon, timestamp: new Date().toISOString(), valoracion, comentario, foto_url: null } });
      } else {
        toast.error("No se pudo registrar el check-in");
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-noc-bg flex flex-col" data-testid="field-checkin-sheet">
      <div className="p-5 flex items-center justify-between border-b border-white/10">
        <p className="text-white font-black text-lg">{stop.nombre}</p>
        <button onClick={onClose} data-testid="close-checkin-sheet" className="text-white/70 text-sm">Cerrar</button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-6 text-white">
        <div className="flex items-center gap-2 text-sm text-valoriza-leaf font-bold">
          <MapPin className="h-4 w-4" /> {coords ? "GPS capturado" : "Obteniendo GPS..."}
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2">Valoración</p>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setValoracion(n)} data-testid={`valoracion-${n}`} className="min-h-[56px] min-w-[56px] rounded-lg border flex items-center justify-center" style={{ borderColor: n <= valoracion ? "#7AB800" : "rgba(255,255,255,0.2)" }}>
                <Star className={`h-6 w-6 ${n <= valoracion ? "text-valoriza-leaf fill-valoriza-leaf" : "text-white/30"}`} />
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center justify-center gap-2 min-h-[64px] rounded-lg border border-white/20 text-white font-bold" data-testid="field-camera-input">
          <Camera className="h-5 w-5" /> {file ? file.name : "Hacer foto"}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
        </label>
        <textarea
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          placeholder="Comentario rápido..."
          className="w-full min-h-[80px] rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white"
          data-testid="field-comentario-textarea"
        />
      </div>
      <div className="p-5">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={confirmar}
          disabled={confirming}
          data-testid="confirm-checkin-button"
          className="w-full min-h-[72px] rounded-xl bg-valoriza-leaf text-black font-black text-lg flex items-center justify-center gap-2"
        >
          <CheckCircle2 className="h-6 w-6" /> {confirming ? "Guardando..." : "Confirmar visita"}
        </motion.button>
      </div>
    </div>
  );
}

function IncidentSheet({ incident, onDone, onClose }) {
  const [comentario, setComentario] = useState("");
  const [fileAntes, setFileAntes] = useState(null);
  const [fileDespues, setFileDespues] = useState(null);
  const [saving, setSaving] = useState(false);
  const siguienteEstado = TRANSICIONES_CAMPO[incident.estado];

  const subirFoto = async (file, tipo) => {
    const fields = { tipo };
    if (!navigator.onLine) {
      await enqueue({ url: `/incidents/${incident._id}/evidencias`, fields, file, label: `Evidencia ${tipo} — ${incident.codigo}` });
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipo);
      await api.post(`/incidents/${incident._id}/evidencias`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) {
      if (!e.response) await enqueue({ url: `/incidents/${incident._id}/evidencias`, fields, file, label: `Evidencia ${tipo} — ${incident.codigo}` });
      else throw e;
    }
  };

  const resolver = async () => {
    setSaving(true);
    const offline = !navigator.onLine;
    try {
      if (fileAntes) await subirFoto(fileAntes, "antes");
      if (fileDespues) await subirFoto(fileDespues, "despues");

      if (siguienteEstado) {
        const jsonPayload = { estado: siguienteEstado, comentario };
        if (offline) {
          await enqueue({ url: `/incidents/${incident._id}/estado`, method: "patch", jsonPayload, label: `Transición ${incident.codigo}` });
        } else {
          try {
            await api.patch(`/incidents/${incident._id}/estado`, jsonPayload);
          } catch (e) {
            if (!e.response) await enqueue({ url: `/incidents/${incident._id}/estado`, method: "patch", jsonPayload, label: `Transición ${incident.codigo}` });
            else throw e;
          }
        }
      }

      toast[offline ? "info" : "success"](offline ? "Sin conexión: guardado localmente, se sincronizará automáticamente" : siguienteEstado ? `Incidencia movida a "${siguienteEstado}"` : "Evidencia guardada");
      onDone({ offline, nuevoEstado: siguienteEstado });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo actualizar la incidencia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-noc-bg flex flex-col" data-testid="field-incident-sheet">
      <div className="p-5 flex items-center justify-between border-b border-white/10">
        <p className="text-white font-black text-lg">{incident.codigo}</p>
        <button onClick={onClose} data-testid="close-incident-sheet" className="text-white/70 text-sm">Cerrar</button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-5 text-white">
        <div>
          <p className="text-xs uppercase tracking-widest text-valoriza-leaf font-bold mb-1">{incident.estado}</p>
          <p className="font-bold">{incident.tematica}</p>
          <p className="text-sm text-white/60 mt-1">{incident.direccion}</p>
        </div>

        {incident.iris_tickets_detail?.length > 0 && (
          <div className="rounded-lg border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2">Heredado de ficha IRIS</p>
            {incident.iris_tickets_detail.map((t) => (
              <div key={t._id} className="text-sm space-y-1 mb-2">
                <p className="font-bold">{t.codigo}</p>
                <p className="text-white/70">{t.area} · {t.elemento} · {t.detalle}</p>
                <p className="text-white/50 text-xs">Barrio: {t.barrio} · Alta: {t.fecha_alta} · Canal: {t.canal_entrada}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col items-center justify-center gap-1 min-h-[72px] rounded-lg border border-white/20 text-white font-bold text-xs" data-testid="field-foto-antes-input">
            <Camera className="h-5 w-5" /> {fileAntes ? fileAntes.name : "Foto ANTES"}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFileAntes(e.target.files[0])} />
          </label>
          <label className="flex flex-col items-center justify-center gap-1 min-h-[72px] rounded-lg border border-white/20 text-white font-bold text-xs" data-testid="field-foto-despues-input">
            <Camera className="h-5 w-5" /> {fileDespues ? fileDespues.name : "Foto DESPUÉS"}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFileDespues(e.target.files[0])} />
          </label>
        </div>

        <textarea
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          placeholder="Describe la resolución..."
          className="w-full min-h-[80px] rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white"
          data-testid="field-incident-comentario-textarea"
        />
      </div>
      <div className="p-5">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={resolver}
          disabled={saving}
          data-testid="confirm-incident-resolution-button"
          className="w-full min-h-[72px] rounded-xl bg-valoriza-leaf text-black font-black text-lg flex items-center justify-center gap-2"
        >
          <CheckCircle2 className="h-6 w-6" /> {saving ? "Guardando..." : siguienteEstado ? `Confirmar → ${siguienteEstado}` : "Guardar evidencia"}
        </motion.button>
      </div>
    </div>
  );
}

function NewIncidentSheet({ onClose, onCreated }) {
  const { user } = useAuth();
  const [tipo, setTipo] = useState(TIPOS_INCIDENCIA[0]);
  const [detalle, setDetalle] = useState("");
  const [direccion, setDireccion] = useState("");
  const [file, setFile] = useState(null);
  const [coords, setCoords] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition((pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }), () => {});
  }, []);

  const crear = async () => {
    if (!detalle.trim()) {
      toast.error("Describe la incidencia");
      return;
    }
    setSaving(true);
    const payload = { origen: "campo", tipo, tematica: detalle, direccion, lat: coords?.lat ?? null, lon: coords?.lon ?? null, responsable_id: user._id };
    try {
      if (!navigator.onLine) throw { offline: true };
      const { data } = await api.post("/incidents", payload);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("tipo", "antes");
        await api.post(`/incidents/${data._id}/evidencias`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Incidencia ${data.codigo} creada`);
      onCreated({ offline: false });
    } catch (e) {
      if (!navigator.onLine || !e.response) {
        await enqueue({
          url: "/incidents",
          jsonPayload: payload,
          chainedFile: file || null,
          chainedFields: file ? { tipo: "antes" } : null,
          chainedUrlTemplate: file ? "/incidents/{id}/evidencias" : null,
          label: `Nueva incidencia: ${detalle}`,
        });
        toast.info(file ? "Sin conexión: incidencia y foto guardadas, se sincronizarán al recuperar cobertura" : "Sin conexión: incidencia guardada, se creará al recuperar cobertura");
        onCreated({
          offline: true,
          local: { ...payload, _id: `local-${Date.now()}`, codigo: "Pendiente de sincronizar", estado: "Nueva", pendienteSync: true },
        });
      } else {
        toast.error(e.response?.data?.detail || "No se pudo crear la incidencia");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-noc-bg flex flex-col" data-testid="field-new-incident-sheet">
      <div className="p-5 flex items-center justify-between border-b border-white/10">
        <p className="text-white font-black text-lg">Nueva incidencia</p>
        <button onClick={onClose} data-testid="close-new-incident-sheet" className="text-white/70 text-sm">Cerrar</button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-5 text-white">
        <div className="flex items-center gap-2 text-sm text-valoriza-leaf font-bold">
          <MapPin className="h-4 w-4" /> {coords ? "GPS capturado" : "Obteniendo GPS..."}
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2">Tipo</p>
          <div className="grid grid-cols-2 gap-2">
            {TIPOS_INCIDENCIA.map((t) => (
              <button
                key={t}
                onClick={() => setTipo(t)}
                data-testid={`field-new-incident-tipo-${t.replace(/[\s/]+/g, "-").toLowerCase()}`}
                className={`min-h-[48px] rounded-lg border px-2 text-xs font-bold ${tipo === t ? "border-valoriza-leaf bg-valoriza-leaf/10 text-valoriza-leaf" : "border-white/15 text-white/70"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <textarea
          value={detalle}
          onChange={(e) => setDetalle(e.target.value)}
          placeholder="Describe la incidencia..."
          className="w-full min-h-[80px] rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white"
          data-testid="field-new-incident-detalle-textarea"
        />
        <input
          value={direccion}
          onChange={(e) => setDireccion(e.target.value)}
          placeholder="Dirección (opcional)"
          className="w-full min-h-[48px] rounded-lg bg-white/5 border border-white/10 p-3 text-sm text-white"
          data-testid="field-new-incident-direccion-input"
        />
        <label className="flex items-center justify-center gap-2 min-h-[64px] rounded-lg border border-white/20 text-white font-bold" data-testid="field-new-incident-camera-input">
          <Camera className="h-5 w-5" /> {file ? file.name : "Hacer foto (opcional)"}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
        </label>
      </div>
      <div className="p-5">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={crear}
          disabled={saving}
          data-testid="confirm-new-incident-button"
          className="w-full min-h-[72px] rounded-xl bg-valoriza-leaf text-black font-black text-lg flex items-center justify-center gap-2"
        >
          <CheckCircle2 className="h-6 w-6" /> {saving ? "Guardando..." : "Crear incidencia"}
        </motion.button>
      </div>
    </div>
  );
}

export default function FieldRoute() {
  const { user, logout } = useAuth();
  const [route, setRoute] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [activeStop, setActiveStop] = useState(null);
  const [activeIncident, setActiveIncident] = useState(null);
  const [creatingIncident, setCreatingIncident] = useState(false);
  const [tab, setTab] = useState("ruta");
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const prevPendientesRef = useRef(null);

  const loadRoute = () =>
    api.get("/routes").then((r) => {
      const mine = r.data.find((rt) => rt.encargado_id === user._id) || r.data[0] || null;
      setRoute(mine);
    });

  const loadIncidents = () =>
    api.get("/incidents").then((r) => {
      setIncidents(r.data);
      const pendientes = r.data.filter((i) => ["Nueva", "Asignada"].includes(i.estado)).length;
      if (prevPendientesRef.current !== null && pendientes > prevPendientesRef.current) {
        toast.info("Nueva incidencia asignada", { icon: <Bell className="h-4 w-4" /> });
      }
      prevPendientesRef.current = pendientes;
    });

  const refreshPendingCount = () => getQueue().then((q) => setPendingCount(q.length));

  useEffect(() => {
    loadRoute();
    loadIncidents();
    const id = setInterval(loadIncidents, 30000);
    return () => clearInterval(id);
  }, [user]);

  // Envía la posición GPS del encargado cada 3 minutos mientras la app de campo está
  // abierta, para alimentar el mapa en vivo de oficina/jefatura de servicio.
  useEffect(() => {
    if (!user || user.role !== "encargado") return;
    const enviarUbicacion = () => {
      if (!navigator.geolocation || !navigator.onLine) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          api.post("/field/location", { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 10000 }
      );
    };
    enviarUbicacion();
    const id = setInterval(enviarUbicacion, 180000);
    return () => clearInterval(id);
  }, [user]);

  // Cola de sincronización: reintenta enviar lo guardado sin conexión en cuanto vuelve
  // la cobertura, o cada 30s si ya está online.
  useEffect(() => {
    refreshPendingCount();
    const trySync = async () => {
      if (!navigator.onLine) return;
      const { synced } = await flushQueue(api);
      if (synced > 0) {
        toast.success(`${synced} elemento(s) sincronizados`);
        loadRoute();
        loadIncidents();
      }
      refreshPendingCount();
    };
    const handleOnline = () => {
      setOnline(true);
      trySync();
    };
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    trySync();
    const id = setInterval(trySync, 30000);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(id);
    };
  }, []);

  const abrirIncidente = async (inc) => {
    if (inc.pendienteSync) {
      toast.info("Esta incidencia aún no se ha sincronizado con el servidor");
      return;
    }
    const { data } = await api.get(`/incidents/${inc._id}`);
    setActiveIncident(data);
  };

  const marcarStopLocalmente = (stopId, checkin) => {
    setRoute((r) => (r ? { ...r, stops: r.stops.map((s) => (s.id === stopId ? { ...s, visitado: true, checkin, pendienteSync: true } : s)) } : r));
  };

  const pendientes = incidents.filter((i) => ["Nueva", "Asignada", "En curso", "Resuelta"].includes(i.estado));
  const visitados = route ? route.stops.filter((s) => s.visitado).length : 0;

  return (
    <div className="min-h-screen bg-noc-bg text-white">
      <div className="p-5 flex items-center justify-between border-b border-white/10">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-valoriza-leaf font-bold">SENTINEL BCN</p>
          <p className="text-xl font-black">{user?.name}</p>
          <div className="flex items-center gap-2 mt-1.5 text-[11px]" data-testid="connectivity-indicator">
            <span className={`h-2 w-2 rounded-full ${online ? "bg-valoriza-leaf" : "bg-accent-alert"}`} />
            <span className="text-white/60">{online ? "En línea" : "Sin conexión"}</span>
            {pendingCount > 0 && (
              <span data-testid="pending-sync-badge" className="ml-1 flex items-center gap-1 rounded-full bg-accent-alert/20 text-accent-alert px-2 py-0.5 font-bold">
                <WifiOff className="h-3 w-3" /> {pendingCount} pendiente{pendingCount > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        <button onClick={logout} data-testid="field-logout-button" className="p-2">
          <LogOut className="h-5 w-5" />
        </button>
      </div>

      <div className="flex border-b border-white/10">
        <button
          onClick={() => setTab("ruta")}
          data-testid="field-tab-ruta"
          className={`flex-1 py-4 flex items-center justify-center gap-2 text-sm font-bold ${tab === "ruta" ? "text-valoriza-leaf border-b-2 border-valoriza-leaf" : "text-white/50"}`}
        >
          <Route className="h-4 w-4" /> Mi ruta
        </button>
        <button
          onClick={() => setTab("incidencias")}
          data-testid="field-tab-incidencias"
          className={`flex-1 py-4 flex items-center justify-center gap-2 text-sm font-bold relative ${tab === "incidencias" ? "text-valoriza-leaf border-b-2 border-valoriza-leaf" : "text-white/50"}`}
        >
          <ClipboardList className="h-4 w-4" /> Incidencias
          {pendientes.length > 0 && (
            <span data-testid="field-incidents-badge" className="absolute top-2 right-6 h-4 min-w-[16px] px-1 rounded-full bg-accent-alert text-[9px] font-bold flex items-center justify-center">
              {pendientes.length}
            </span>
          )}
        </button>
      </div>

      {tab === "ruta" && (
        <div className="p-5">
          {!route ? (
            <p className="text-center text-white/60 mt-10" data-testid="field-no-route">No tienes ninguna ruta de calidad asignada hoy.</p>
          ) : (
            <>
              <p className="text-lg font-black mb-1">{route.nombre}</p>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden mb-4 mt-3">
                <div className="h-full bg-valoriza-leaf transition-all" style={{ width: `${(visitados / route.stops.length) * 100}%` }} />
              </div>
              <p className="text-sm text-white/60 mb-4">{visitados} de {route.stops.length} puntos visitados</p>
              <div className="space-y-3" data-testid="field-stops-list">
                {route.stops.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => !s.visitado && setActiveStop(s)}
                    data-testid={`field-stop-${s.id}`}
                    className={`w-full text-left min-h-[64px] rounded-xl border p-4 flex items-center justify-between ${s.visitado ? "border-valoriza-leaf/40 bg-valoriza-leaf/10" : "border-white/15 active:bg-white/5"}`}
                  >
                    <div>
                      <p className="font-bold">{s.nombre}</p>
                      <p className="text-xs text-white/50">{s.ventana_inicio} - {s.ventana_fin}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {s.pendienteSync && <span className="text-[9px] uppercase font-bold text-accent-alert">Pendiente sync</span>}
                      {s.visitado ? <CheckCircle2 className="h-6 w-6 text-valoriza-leaf" /> : <MapPin className="h-6 w-6 text-white/40" />}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "incidencias" && (
        <div className="p-5 space-y-3" data-testid="field-incidents-list">
          {pendientes.length === 0 && <p className="text-center text-white/60 mt-10">No tienes incidencias asignadas.</p>}
          {pendientes.map((inc) => (
            <button
              key={inc._id}
              onClick={() => abrirIncidente(inc)}
              data-testid={`field-incident-${inc._id}`}
              className="w-full text-left min-h-[72px] rounded-xl border border-white/15 p-4 active:bg-white/5"
            >
              <div className="flex items-center justify-between">
                <p className="font-bold">{inc.codigo}</p>
                <span className="text-[10px] uppercase tracking-wide text-valoriza-leaf font-bold">{inc.pendienteSync ? "Pendiente sync" : inc.estado}</span>
              </div>
              <p className="text-sm text-white/70 mt-1 line-clamp-1">{inc.tematica}</p>
              <p className="text-xs text-white/40 mt-0.5">{inc.direccion}</p>
            </button>
          ))}
        </div>
      )}

      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={() => setCreatingIncident(true)}
        data-testid="field-new-incident-fab"
        className="fixed bottom-6 right-6 z-40 h-16 w-16 rounded-full bg-valoriza-leaf text-black shadow-xl flex items-center justify-center"
      >
        <Plus className="h-7 w-7" />
      </motion.button>

      {activeStop && (
        <StopSheet
          stop={activeStop}
          routeId={route._id}
          onClose={() => setActiveStop(null)}
          onDone={(info) => {
            const stopId = activeStop.id;
            setActiveStop(null);
            if (info?.synced === false) {
              marcarStopLocalmente(stopId, info.checkin);
            } else {
              loadRoute();
            }
            refreshPendingCount();
          }}
        />
      )}
      {activeIncident && (
        <IncidentSheet
          incident={activeIncident}
          onClose={() => setActiveIncident(null)}
          onDone={(info) => {
            const id = activeIncident._id;
            setActiveIncident(null);
            if (info?.offline && info?.nuevoEstado) {
              setIncidents((list) => list.map((i) => (i._id === id ? { ...i, estado: info.nuevoEstado, pendienteSync: true } : i)));
            } else {
              loadIncidents();
            }
            refreshPendingCount();
          }}
        />
      )}
      {creatingIncident && (
        <NewIncidentSheet
          onClose={() => setCreatingIncident(false)}
          onCreated={(info) => {
            setCreatingIncident(false);
            if (info?.offline && info?.local) {
              setIncidents((list) => [info.local, ...list]);
            } else {
              loadIncidents();
            }
            refreshPendingCount();
          }}
        />
      )}
    </div>
  );
}
